import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Check, Calculator, Search, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTheme } from '../context/ThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ToolSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { isDark } = useTheme();
  const [status, setStatus] = useState('processing');

  const sessionId = searchParams.get('session_id');
  const ref = searchParams.get('ref'); // new: ref=sid:... or ref=email:...
  const paymentId = searchParams.get('paymentId');
  const payerId = searchParams.get('PayerID');

  // If ref is email-based, extract for the post-purchase My Profile link
  const refIsEmail = ref && ref.startsWith('email:');
  const buyerEmail = refIsEmail ? ref.slice(6) : null;
  // Either an explicit session_id (legacy path) or extracted from ref=sid:
  const refSession = ref && ref.startsWith('sid:') ? ref.slice(4) : null;
  const effectiveSessionId = sessionId || refSession;

  useEffect(() => {
    if (paymentId && payerId && (effectiveSessionId || buyerEmail)) {
      executePayment();
    } else {
      setStatus('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentId, payerId, effectiveSessionId, buyerEmail]);

  const executePayment = async () => {
    try {
      await axios.get(`${API}/tools/execute-payment`, {
        params: {
          paymentId,
          PayerID: payerId,
          ...(effectiveSessionId ? { session_id: effectiveSessionId } : {}),
          ...(ref ? { ref } : {}),
        }
      });
      setStatus('success');
      localStorage.removeItem('rv_tool_offer_start');
    } catch {
      setStatus('error');
    }
  };

  const bg = isDark ? '#0a0a0f' : '#f8f9fa';
  const textPrimary = isDark ? '#f0f0f5' : '#111827';
  const textSecondary = isDark ? '#8b8ba3' : '#6b7280';

  return (
    <div data-testid="tool-success-page" style={{ background: bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', -apple-system, sans-serif" }}>
      <div style={{ textAlign: 'center', maxWidth: 500, padding: 24 }}>
        {status === 'processing' && (
          <>
            <Loader2 className="w-12 h-12 animate-spin" style={{ color: '#F2A900', margin: '0 auto' }} />
            <h2 style={{ color: textPrimary, fontSize: 22, fontWeight: 700, marginTop: 20 }}>Unlocking your tools...</h2>
            <p style={{ color: textSecondary, fontSize: 15 }}>Please wait while we confirm your payment.</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#22c55e15', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
              <Check size={36} style={{ color: '#22c55e' }} />
            </div>
            <h2 style={{ color: textPrimary, fontSize: 26, fontWeight: 800, marginTop: 20 }}>You're All Set!</h2>
            <p style={{ color: textSecondary, fontSize: 15, marginTop: 8 }}>
              Your 3 Pro Tools are unlocked and saved to your profile.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 24, flexWrap: 'wrap' }}>
              {effectiveSessionId ? (
                <>
                  <Link to={`/vip/${effectiveSessionId}/cost-calculator`}>
                    <Button style={{ background: '#22c55e', color: '#fff', fontWeight: 600, border: 'none' }}>
                      <Calculator size={16} style={{ marginRight: 6 }} /> Cost Calculator
                    </Button>
                  </Link>
                  <Link to={`/vip/${effectiveSessionId}/deal-analyzer`}>
                    <Button style={{ background: '#3b82f6', color: '#fff', fontWeight: 600, border: 'none' }}>
                      <Search size={16} style={{ marginRight: 6 }} /> Deal Analyzer
                    </Button>
                  </Link>
                </>
              ) : (
                <Link to={buyerEmail ? `/my-quiz?email=${encodeURIComponent(buyerEmail)}` : '/my-quiz'}>
                  <Button style={{ background: '#F2A900', color: '#000', fontWeight: 700, border: 'none' }}>
                    Open My Profile <ArrowRight size={16} style={{ marginLeft: 6 }} />
                  </Button>
                </Link>
              )}
            </div>
          </>
        )}
        {status === 'error' && (
          <>
            <h2 style={{ color: '#ef4444', fontSize: 22, fontWeight: 700, marginTop: 20 }}>Payment Issue</h2>
            <p style={{ color: textSecondary, fontSize: 15, marginTop: 8 }}>We couldn't confirm your payment. Please try again or contact support.</p>
            <Link to={effectiveSessionId ? `/my-matches/${effectiveSessionId}` : '/tools-bundle'}>
              <Button style={{ marginTop: 16 }}>
                <ArrowRight size={16} style={{ marginRight: 6 }} /> Try Again
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default ToolSuccessPage;
